import { newUi } from "./featureFlags";

/**
 * The guided tour (#1514; D406, D407, D407a). Two routes, one per role, and a
 * per-device "seen" flag. The flag lives in `localStorage`, not on the account
 * (D406): a new device shows the tour once, and a tour on one device never
 * hides it on another. Same fail-open shape as `lib/voice.js`'s explainer flag
 * (D116): storage that throws (a private window, policy-blocked storage) reads
 * as "not seen" and writes nothing, so the screen never crashes and the tour
 * simply shows again next time.
 */
export const TOUR_PATH = "/tour";
export const COACH_TOUR_PATH = "/tour/coach";

export const TOUR_ROLES = Object.freeze(["player", "coach"]);

/** The replay marker (Settings > More > Take the tour). A replay never writes the flag. */
export const TOUR_REPLAY_PARAM = "replay";

const seenKey = (role) => `nw_tour_seen:${role}`;

export function tourPathFor(role) {
  return role === "coach" ? COACH_TOUR_PATH : TOUR_PATH;
}

export function tourReplayHref(role) {
  return `${tourPathFor(role)}?${TOUR_REPLAY_PARAM}=1`;
}

/** Has this device been through the tour for `role`? Throwing storage reads as unseen. */
export function tourSeen(role) {
  try {
    return window.localStorage.getItem(seenKey(role)) === "true";
  } catch {
    return false;
  }
}

/** Set on Skip and on the last step's actions; never by a replay (the caller decides). */
export function markTourSeen(role) {
  try {
    window.localStorage.setItem(seenKey(role), "true");
  } catch {
    // Storage blocked or full: the tour simply shows again next time.
  }
}

/**
 * Should the set-up that just finished hand over to the tour? Only on the new
 * look (the old look navigates exactly as before, D217) and only while this
 * device has not seen the role's tour.
 */
export function shouldShowTour(role) {
  return newUi("tour") && !tourSeen(role);
}

/**
 * The hand-over from a finished set-up: to the tour when it is due (`state.next`
 * carries where the person was going, so the tour's Skip and last card
 * continue to it), else straight to `target`, exactly as before. The one place
 * a set-up screen names the tour, so it holds no route literal of its own.
 */
export function leaveSetUpTo(nav, role, target) {
  if (shouldShowTour(role)) nav(tourPathFor(role), { replace: true, state: { next: target } });
  else nav(target, { replace: true });
}
